import {
  MessageSquare,
  Search,
  CheckCircle2,
  MailOpen,
  Mail,
  User,
  Clock,
  ExternalLink,
  Phone,
  Trash2
} from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/Modal";
import { getAdminEnquiries, updateEnquiryStatus, deleteEnquiry } from "@/lib/api";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { CustomerDetail } from "./AdminCustomers";

export function AdminEnquiries() {
  const { customers, orders } = useStore();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [viewingCustomerProfile, setViewingCustomerProfile] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const data = await getAdminEnquiries();
      setEnquiries(data || []);
    } catch (err) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await updateEnquiryStatus(id, status);
      toast.success("Status updated");
      fetchEnquiries();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      await deleteEnquiry(id);
      toast.success("Enquiry deleted");
      if (viewing?._id === id) setViewing(null);
      fetchEnquiries();
    } catch (err) {
      toast.error("Failed to delete enquiry");
    }
  };

  const filtered = useMemo(() => {
    return enquiries.filter((enq) => {
      const matchesSearch =
        enq.name.toLowerCase().includes(search.toLowerCase()) ||
        enq.email.toLowerCase().includes(search.toLowerCase()) ||
        (enq.phone && enq.phone.includes(search));
      
      let matchesStatus = true;
      if (statusFilter !== "all") {
        matchesStatus = enq.status.toLowerCase() === statusFilter.toLowerCase();
      }
      return matchesSearch && matchesStatus;
    });
  }, [enquiries, search, statusFilter]);

  const totals = useMemo(() => {
    return {
      total: enquiries.length,
      new: enquiries.filter((e) => e.status === "New").length,
      read: enquiries.filter((e) => e.status === "Read").length,
      resolved: enquiries.filter((e) => e.status === "Resolved").length,
    };
  }, [enquiries]);

  return (
    <div className="space-y-6 max-w-5xl">
      {viewing ? (
        // Detail View Page
        <div className="animate-fade-in space-y-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setViewing(null)}
              className="flex items-center gap-2 text-[#4A4652] hover:text-[#1C1924] transition font-medium text-[14px]"
            >
              &larr; Back to Enquiries
            </button>
            <div className="flex items-center gap-2">
              {viewing.status === "New" && <Badge tone="primary">New</Badge>}
              <span className="text-[12px] text-[#8b8790]">
                {new Date(viewing.createdAt).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white border border-[#E8DFD0] rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-[#E8DFD0] bg-[#FAF8F5]">
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center text-[16px] font-bold text-white shadow-sm shrink-0",
                    viewing.status === "New" ? "bg-[#E5497C]" : "bg-[#1C1924]",
                  )}
                >
                  {viewing.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-[16px] font-bold text-[#1C1924] flex items-center gap-2">
                    {viewing.name}
                    {viewing.user && (
                      <Badge tone="success">Registered User</Badge>
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-[13px] text-[#4A4652] mt-1">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" /> {viewing.email}
                    </span>
                    {viewing.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" /> {viewing.phone}
                      </span>
                    )}
                  </div>
                </div>
                {viewing.user && (
                  <button 
                    onClick={() => {
                      const customerOrders = orders.filter(order => {
                        const cPhone = viewing.phone ? viewing.phone.replace(/\D/g, '') : null;
                        const oPhone = order.shippingAddress?.phone ? order.shippingAddress.phone.replace(/\D/g, '') : null;
                        const phoneMatch = cPhone && oPhone && (cPhone === oPhone || oPhone.endsWith(cPhone) || cPhone.endsWith(oPhone));
                        const emailMatch = viewing.email && order.userEmail && viewing.email.toLowerCase() === order.userEmail.toLowerCase();
                        return phoneMatch || emailMatch;
                      });
                      const totalSpend = customerOrders.reduce((sum, o) => sum + (o.total || 0), 0) || viewing.user.totalSpending || 0;
                      setViewingCustomerProfile({
                        ...viewing.user,
                        customerOrders,
                        totalSpending: totalSpend
                      });
                    }}
                    className="flex items-center gap-1.5 text-[12px] font-medium text-[#1C1924] hover:text-[#E5497C] transition px-3 py-1.5 border border-[#E8DFD0] bg-white rounded-lg"
                  >
                    <User className="w-3.5 h-3.5" /> View Profile
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              <label className="text-[12px] font-bold uppercase tracking-wider text-[#8b8790] block mb-3">
                Message Content
              </label>
              <div className="bg-[#FAF8F5] p-5 rounded-xl border border-[#E8DFD0] text-[14.5px] text-[#1C1924] whitespace-pre-wrap leading-relaxed shadow-sm">
                {viewing.message}
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-[#E8DFD0] bg-white">
              <button
                onClick={() => handleDeleteEnquiry(viewing._id)}
                className="px-4 py-2 text-red-500 rounded-xl text-[14px] font-medium hover:bg-red-50 transition flex items-center gap-2 border border-transparent hover:border-red-100"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
              
              <button
                onClick={() => {
                  if (viewing.status === "New") {
                    handleUpdateStatus(viewing._id, "Read");
                  }
                  setViewing(null);
                }}
                className="px-6 py-2.5 bg-[#1C1924] text-white rounded-xl text-[14px] font-medium hover:bg-[#E5497C] transition flex items-center gap-2 shadow-sm"
              >
                <CheckCircle2 className="h-4 w-4" />
                Done
              </button>
            </div>
          </div>
        </div>
      ) : (
        // List View
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-[#1C1924]">
                Enquiries
              </h2>
              <p className="text-sm text-[#4A4652] mt-1">
                Manage and respond to customer queries.
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white border border-[#E8DFD0] rounded-2xl p-4 shadow-sm">
              <p className="text-[12px] font-semibold text-[#8b8790] uppercase tracking-wider mb-1">
                Total Queries
              </p>
              <p className="text-2xl font-bold text-[#1C1924]">{totals.total}</p>
            </div>
            <div className="bg-white border border-[#E8DFD0] rounded-2xl p-4 shadow-sm">
              <p className="text-[12px] font-semibold text-[#8b8790] uppercase tracking-wider mb-1">
                New
              </p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-[#E5497C]">{totals.new}</p>
              </div>
            </div>
            <div className="bg-white border border-[#E8DFD0] rounded-2xl p-4 shadow-sm">
              <p className="text-[12px] font-semibold text-[#8b8790] uppercase tracking-wider mb-1">
                Read
              </p>
              <p className="text-2xl font-bold text-[#C4A55A]">{totals.read}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b8790] h-4 w-4" />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-[#E8DFD0] rounded-xl text-[14px] text-[#1C1924] focus:outline-none focus:border-[#E5497C] transition-colors"
              />
            </div>
            <div className="flex gap-2 bg-white border border-[#E8DFD0] p-1 rounded-xl">
              {["all", "New", "Read"].map((t) => (
                <button
                  key={t}
                  onClick={() => setStatusFilter(t)}
                  className={cn(
                    "px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all capitalize",
                    statusFilter === t
                      ? "bg-[#1C1924] text-white shadow-sm"
                      : "text-[#4A4652] hover:bg-[#FAF8F5]",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Enquiries List */}
          <div className="bg-white border border-[#E8DFD0] rounded-2xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-[#8b8790] text-sm">
                Loading enquiries...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-[#8b8790] text-sm">
                No enquiries found matching your criteria.
              </div>
            ) : (
              <div className="divide-y divide-[#E8DFD0]">
                {filtered.map((enq) => (
                  <button
                    key={enq._id}
                    onClick={() => {
                      setViewing(enq);
                      if (enq.status === "New") handleUpdateStatus(enq._id, "Read");
                    }}
                    className={cn(
                      "w-full text-left p-4 transition-colors flex flex-col sm:flex-row gap-4 sm:items-center hover:bg-[#FAF8F5]",
                      enq.status === "New" ? "bg-[#FFF4F7]/40" : "bg-white",
                    )}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-bold text-white shadow-sm shrink-0",
                          enq.status === "New" ? "bg-[#E5497C]" : "bg-[#1C1924]",
                        )}
                      >
                        {enq.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-[14px] font-bold text-[#1C1924]">
                            {enq.name}
                          </p>
                          {enq.status === "New" && (
                            <Badge tone="primary">New</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-[13px] text-[#4A4652] mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {enq.email}
                          </span>
                          {enq.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {enq.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 sm:w-[400px] sm:justify-end">
                      <p className="text-[13px] text-[#8b8790] line-clamp-2 flex-1 sm:text-right hidden sm:block">
                        {enq.message}
                      </p>
                      <div className="shrink-0 w-8 h-8 rounded-lg border border-[#E8DFD0] flex items-center justify-center text-[#8b8790] bg-white group-hover:border-[#1C1924] group-hover:text-[#1C1924] transition-colors">
                        <ExternalLink className="h-4 w-4" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Viewing Customer Full Profile Modal */}
      {viewingCustomerProfile && (
        <CustomerDetail 
          customer={viewingCustomerProfile} 
          orders={viewingCustomerProfile.customerOrders} 
          onClose={() => setViewingCustomerProfile(null)} 
        />
      )}
    </div>
  );
}
